import { Component, Input } from '@angular/core';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { LeadsService } from '@services/api/leads.service';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import { ContactAlternativeRow } from './contact-alternative-info.model';

@Component({
  selector: 'app-contact-alternative-info',
  templateUrl: './contact-alternative-info.component.html',
  styleUrls: ['./contact-alternative-info.component.scss'],
})
export class ContactAlternativeInfoComponent {
  // Parent owns the array; rows are pushed/spliced in place so the parent's save reads it directly.
  @Input() items: ContactAlternativeRow[] = [];
  @Input() isReadonly: boolean = false;
  /** The contact being edited; a lead-linked row is only unlinked from this contact. */
  @Input() contactId: string = null;

  readonly labelOptions: string[] = ['Personal', 'Work', 'Home', 'Spouse', 'Partner', 'Other'];
  private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  removingIndex: number = null;

  constructor(
    private leadsService: LeadsService,
    private toastr: ToastrService,
    private remmiConfirm: RemmiConfirmService
  ) {}

  addAlternative() {
    this.items.push({ name: '', email: '', mobile_no: '', label: null });
  }

  removeAlternative(index: number) {
    const item = this.items[index];
    if (!item?.lead_id) {
      this.items.splice(index, 1);
      return;
    }
    this.remmiConfirm
      .delete({
        title: 'Remove Alternate Contact?',
        message: 'The lead will be unlinked and remain as a separate contact. Press Save on this contact to finish removing.',
        subject: item.name || item.email || item.mobile_no,
        confirmLabel: 'Remove',
        icon: 'pi pi-user-minus',
      })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.unlinkAndRemove(item);
        }
      });
  }

  /**
   * A lead-linked row (REM-3497) protects that lead from the contact-save sync only while it
   * exists. Dropping the row alone would leave the lead linked and let the next save overwrite it,
   * so the lead is unlinked first through the same endpoint as the lead page's "Remove Assigned".
   * It is only unlinked when it still points at this contact; a lead since linked elsewhere is
   * left alone and only the row goes.
   */
  private unlinkAndRemove(item: ContactAlternativeRow) {
    const leadId = item.lead_id;
    this.removingIndex = this.items.indexOf(item);
    this.leadsService
      .getLeadById(leadId)
      .pipe(
        switchMap(response => {
          const isLinkedHere = !!this.contactId && String(response?.data?.contactid ?? '') === String(this.contactId);
          return isLinkedHere ? this.leadsService.getAssignment(leadId) : of(null);
        })
      )
      .subscribe({
        next: () => {
          this.removingIndex = null;
          const index = this.items.indexOf(item);
          if (index > -1) {
            this.items.splice(index, 1);
          }
          this.toastr.success('Lead Unlinked. Press Save To Remove The Alternate Contact.');
        },
        error: () => {
          this.removingIndex = null;
          this.toastr.error('Could Not Remove The Alternate Contact. Please Try Again.');
        },
      });
  }

  /**
   * appMobileMask rewrites the input's value but only syncs it with `emitEvent: false`, which
   * doesn't reach an `[(ngModel)]` binding. Whether ngModel then sees the raw or formatted text
   * depends on host-listener order, so read the formatted value back after the mask has run
   * (template listeners fire after directive host listeners).
   */
  onMobileInput(item: ContactAlternativeRow, formatted: string) {
    item.mobile_no = formatted;
  }

  isInvalidEmail(item: ContactAlternativeRow): boolean {
    return !!item.email?.trim() && !this.emailPattern.test(item.email.trim());
  }

  addCustomLabel = (label: string) => label;
}
